import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import BackArrow from "@/shared/assets/icons/back-arrow.png";
import { Graph } from "./Graph";
import { Input } from "@/shared/ui/input";
import toast from "react-hot-toast";
import { Preloader } from "@/components";
import { useQuizFindAnswerMutation, useCalculateWeightLossDateMutation } from "@/store/usersApi";
import { tg } from "@/constants/telegram";
import { useYandexMetrika, GOALS } from "@/hooks/useYandexMetrika";
import { isQuizNotCompletedError, notifyQuizNotCompleted } from "@/shared/utils";
import { useAppSelector } from "@/shared/hooks";

type FormData = {
  age: number | undefined;
  weight: number | undefined;
  height: number | undefined;
};

type CalculationResult = {
  date_to_goal?: string | null;
  muscle_gain?: number | null;
  target_weight?: number | null;
  chartData?: Array<{
    date: string;
    weight: number;
  }>;
};

const getCurrentBodyFatPercentage = (bodyFormSelection: number): number => {
  const bodyFatMapping = {
    1: 5,
    2: 8.5,
    3: 13,
    4: 19.5,
    5: 27,
    6: 35.5,
    7: 45,
  };
  
  return bodyFatMapping[bodyFormSelection as keyof typeof bodyFatMapping] || 20;
};

const getDesiredBodyFatPercentage = (bodyFormSelection: number): number => {
  const bodyFatMapping = {
    1: 5,
    2: 8.5,
    3: 13,
    4: 19.5,
    5: 27,
    6: 35.5,
    7: 45,
  };
  
  return bodyFatMapping[bodyFormSelection as keyof typeof bodyFatMapping] || 20;
};

const generateChartData = (currentWeight: number, targetWeight: number, dateToGoal: string): Array<{
  date: string;
  weight: number;
}> => {
  const startDate = new Date();
  const endDate = new Date(dateToGoal);
  const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  const weightLoss = currentWeight - targetWeight;
  const dailyWeightLoss = weightLoss / totalDays;
  
  const chartData: Array<{
    date: string;
    weight: number;
  }> = [];
  const milestones = [0, 0.25, 0.5, 0.75, 1];
  
  milestones.forEach((progress, index) => {
    const daysPassed = Math.round(totalDays * progress);
    const date = new Date(startDate);
    date.setDate(date.getDate() + daysPassed);
    
    const weightAtProgress = currentWeight - (weightLoss * progress);
    
    chartData.push({
      date: index === 0 ? "Сегодня" : 
            index === milestones.length - 1 ? "Цель" :
            date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }),
      weight: Math.round(weightAtProgress * 10) / 10
    });
  });
  
  return chartData;
};

export const WayToGoal = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [calculationResult, setCalculationResult] = useState<CalculationResult | null>(null);
  const [desiredBodyFat, setDesiredBodyFat] = useState<number | null>(null);
  const [currentBodyFat, setCurrentBodyFat] = useState<number | null>(null);
  
  const [quizFindAnswer] = useQuizFindAnswerMutation();
  const [calculateWeightLossDate] = useCalculateWeightLossDateMutation();
  const { trackAction } = useYandexMetrika();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  const { control, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      age: undefined,
      weight: undefined,
      height: undefined,
    }
  });

  useEffect(() => {
    const loadUserData = async () => {
      const hasAuth = tg?.initData || isAuthenticated || localStorage.getItem("access_token");
      if (!hasAuth) {
        toast.error('Ошибка авторизации');
        return;
      }

      trackAction(GOALS.WAY_TO_GOAL_STARTED);

      try {
        const userData = await quizFindAnswer({ 
          data_type: ['age', 'height', 'weight', 'desired_fat_percentage', 'current_fat_percentage'] 
        }).unwrap();
        
        if (userData.answers) {
          const age = Number(userData.answers.age);
          const weight = Number(userData.answers.weight);
          const height = Number(userData.answers.height);
          const desiredFatPercentage = Number(userData.answers.desired_fat_percentage);
          const currentFatPercentage = Number(userData.answers.current_fat_percentage);
          
          if (age && age > 0) {
            setValue('age', age);
          }
          if (weight && weight > 0) {
            setValue('weight', weight);
          }
          if (height && height > 0) {
            setValue('height', height);
          }
          
          if (desiredFatPercentage && desiredFatPercentage > 0) {
            const desiredFatPercent = getDesiredBodyFatPercentage(desiredFatPercentage);
            setDesiredBodyFat(desiredFatPercent);
          }
          
          if (currentFatPercentage && currentFatPercentage > 0) {
            const currentFatPercent = getCurrentBodyFatPercentage(currentFatPercentage);
            setCurrentBodyFat(currentFatPercent);
          }
        }
      } catch (error) {
        if (isQuizNotCompletedError(error)) {
          notifyQuizNotCompleted();
          navigate("/quiz");
        } else {
          console.error('Ошибка загрузки данных пользователя:', error);
          toast.error('Ошибка загрузки данных пользователя');
        }
      }
    };

    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const onSubmit = async (data: FormData) => {
    const hasAuth = tg?.initData || isAuthenticated || localStorage.getItem("access_token");
    if (!hasAuth) {
      toast.error('Ошибка авторизации');
      return;
    }

    if (!data.age || !data.weight || !data.height) {
      toast.error('Пожалуйста, заполните все поля');
      return;
    }

    if (!desiredBodyFat) {
      toast.error('Данные о желаемом проценте жира не найдены. Пожалуйста, пройдите опрос о форме тела.');
      return;
    }
    
    if (!currentBodyFat) {
      toast.error('Данные о текущем проценте жира не найдены');
      return;
    }

    setIsLoading(true);
    try {
      const result = await calculateWeightLossDate({
        age: data.age,
        weight: data.weight,
        height: data.height,
        current_fat_percentage: Math.round(currentBodyFat),
        desired_fat_percentage: Math.round(desiredBodyFat),
      }).unwrap();
      
      if (!result.date_to_goal || !result.target_weight) {
        toast.error('Неполные данные расчета');
        return;
      }
      
      const chartData = generateChartData(data.weight, result.target_weight, result.date_to_goal);
      
      setCalculationResult({
        ...result,
        chartData
      });
      setShowForm(false);
      
      trackAction(GOALS.WAY_TO_GOAL_COMPLETED, {
        age: data.age,
        weight: data.weight,
        height: data.height,
        currentBodyFat: Math.round(currentBodyFat),
        desiredBodyFat: Math.round(desiredBodyFat),
        targetWeight: result.target_weight,
        dateToGoal: result.date_to_goal,
        muscleGain: result.muscle_gain
      });
      
      toast.success("Расчет завершен!");
    } catch (error) {
      console.error('Ошибка расчета:', error);
      toast.error('Ошибка при расчете');
    } finally {
      setIsLoading(false);
    }
  };

  const finishOnboarding = () => {
    navigate("/trains");
    toast.success("Вперед к цели!");
  };

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="flex flex-col gap-3 pt-10 w-[90%] mx-auto max-w-[600px]">
      <header className="flex items-center gap-3 justify-between mb-6">
        <Link to="/onboarding?step=7">
          <img
            src={BackArrow}
            className="w-[20px] cursor-pointer"
            alt="backArrow"
          />
        </Link>

        <div className="flex gap-1">
          {Array.from({ length: 4 }).map((_, index) => (
            <span
              key={index}
              className="w-10 h-[3px] bg-[#4E7EFB] rounded-full"
            ></span>
          ))}
        </div>

        <div></div>
      </header>

      <main>
        {showForm ? (
          <div>
            <h3 className="text-[28px] leading-[38px] mb-6">
              Давайте рассчитаем ваш путь к цели
            </h3>
            <p className="text-[18px] text-[#525252] mb-8">
              Введите ваши данные для более точного расчета
            </p>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-[16px] font-medium text-[#3c3c3c] mb-2">
                  Возраст (лет)
                </label>
                <Input
                  name="age"
                  control={control}
                  type="number"
                  placeholder="Введите ваш возраст"
                  rules={{
                    required: "Возраст обязателен",
                    validate: (value) => {
                      if (!value) return "Возраст обязателен";
                      if (value < 16) return "Минимальный возраст 16 лет";
                      if (value > 100) return "Максимальный возраст 100 лет";
                      return true;
                    }
                  }}
                />
                {errors.age && (
                  <p className="text-red-500 text-[14px] mt-1">{errors.age.message}</p>
                )}
              </div>

              <div>
                <label className="block text-[16px] font-medium text-[#3c3c3c] mb-2">
                  Вес (кг)
                </label>
                <Input
                  name="weight"
                  control={control}
                  type="number"
                  step="0.1"
                  placeholder="Введите ваш вес"
                  rules={{
                    required: "Вес обязателен",
                    validate: (value) => {
                      if (!value) return "Вес обязателен";
                      if (value < 30) return "Минимальный вес 30 кг";
                      if (value > 300) return "Максимальный вес 300 кг";
                      return true;
                    }
                  }}
                />
                {errors.weight && (
                  <p className="text-red-500 text-[14px] mt-1">{errors.weight.message}</p>
                )}
              </div>

              <div>
                <label className="block text-[16px] font-medium text-[#3c3c3c] mb-2">
                  Рост (см)
                </label>
                <Input
                  name="height"
                  control={control}
                  type="number"
                  placeholder="Введите ваш рост"
                  rules={{
                    required: "Рост обязателен",
                    validate: (value) => {
                      if (!value) return "Рост обязателен";
                      if (value < 120) return "Минимальный рост 120 см";
                      if (value > 250) return "Максимальный рост 250 см";
                      return true;
                    }
                  }}
                />
                {errors.height && (
                  <p className="text-red-500 text-[14px] mt-1">{errors.height.message}</p>
                )}
              </div>

              {desiredBodyFat && (
                <div className="bg-blue-100 rounded-[16px] py-4 px-6 mb-6">
                  <div className="text-[16px] flex gap-2 items-center">
                    <span className="pt-1">Ваш желаемый процент жира в теле: <strong>{desiredBodyFat}%</strong></span>
                  </div>
                </div>
              )}

              <div className="flex justify-center mt-8">
                <button
                  type="submit"
                  className="
                    bg-gradient-to-l from-gray-800 to-gray-500 
                    text-white py-3 rounded-full text-center px-16 text-[22px]
                    shadow-md hover:shadow-lg 
                    active:scale-95 active:brightness-90
                    transition-all duration-200
                  "
                >
                  Рассчитать
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div>
            <p className="mb-3 text-[20px]">
              Более точное предсказание готово! <br />
              Вы достигнете цели
            </p>
            {calculationResult?.date_to_goal && (
              <h3 className="text-[28px] leading-[38px] mb-3">
                <span className="text-[#3164e3]">
                  {new Date(calculationResult.date_to_goal).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </h3>
            )}
            
            {calculationResult?.muscle_gain !== null && calculationResult?.muscle_gain !== undefined ? (
              <div className="mb-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-[20px] p-6 border-2 border-green-200 shadow-lg">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-green-500 rounded-full p-4">
                      <svg 
                        className="w-12 h-12 text-white" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" 
                        />
                      </svg>
                    </div>
                  </div>
                  <h4 className="text-[20px] font-semibold text-[#3c3c3c] mb-3 text-center">
                    Скорость набора веса
                  </h4>
                  <div className="text-center">
                    <p className="text-[42px] font-bold text-green-600 mb-2">
                      +{Math.round(calculationResult.muscle_gain * 10) / 10}
                    </p>
                    <p className="text-[18px] text-[#525252] font-medium">
                      кг в месяц
                    </p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <p className="text-[14px] text-[#666] text-center">
                      При такой скорости набора мышечной массы вы достигнете цели в установленный срок
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {calculationResult?.chartData && calculationResult.chartData.length > 0 && (
                  <div className="mb-6">
                    {calculationResult?.target_weight && (
                      <div className="bg-blue-100 rounded-[16px] py-4 px-6 mb-6">
                        <h4 className="text-[18px] font-medium text-[#3c3c3c] mb-2">
                          Целевой вес
                        </h4>
                        <p className="text-[24px] font-bold text-[#3164e3]">
                          {Math.round(calculationResult.target_weight * 10) / 10} кг
                        </p>
                      </div>
                    )}
                    <Graph data={calculationResult.chartData} />
                  </div>
                )}
              </>
            )}
            
            <p className="text-[20px] text-[#525252] text-center mt-3">
              С персонализировнным планом у тебя есть большой потенциал достичь
              целей в установленные сроки!
            </p>
            
            <div className="bg-gray-50 rounded-[16px] py-4 px-6 mb-6 mt-6">
              <h4 className="text-[18px] font-medium text-[#3c3c3c] mb-3">
                Научные основы расчетов:
              </h4>
              <ul className="space-y-2 text-[14px] text-[#525252]">
                <li className="flex items-start gap-2">
                  <span className="text-[#3164e3] font-bold">•</span>
                  <span>
                    <strong>Формула Миффлина-Сан Жеора (1990):</strong> National Library of Medicine
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#3164e3] font-bold">•</span>
                  <span>
                    <strong>Метаболические расчеты:</strong> AJP Endocrinology
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#3164e3] font-bold">•</span>
                  <span>
                    <strong>Системы подсчета калорий:</strong> CEUR Workshop
                  </span>
                </li>
              </ul>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={() => finishOnboarding()}
                className="
        bg-gradient-to-l from-gray-800 to-gray-500 
        text-white py-3 rounded-full text-center mt-3 px-16 text-[22px]
        shadow-md hover:shadow-lg 
        active:scale-95 active:brightness-90
        transition-all duration-200
      "
              >
                Следующие
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

